import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const SLIDES = [
  { kicker: "Skincare", to: "/shop/skincare", headline: "Get the skin you want to feel", image: "/media/hero/curls.webp", mImage: "/media/hero/curls-m.webp", alt: "Smiling woman with curly hair and cream on her cheek", bg: "#A3724B", w: 1381, h: 1139, mw: 2200, mh: 1814, layout: "right", textW: "lg:w-[41%]" },
  { kicker: "For everyone", to: "/collections", headline: "Beauty for every you", image: "/media/hero/duo.webp", mImage: "/media/hero/duo-m.webp", alt: "Two women laughing together", bg: "#CD8484", w: 1634, h: 963, mw: 2200, mh: 1296, layout: "right", textW: "lg:w-[42%]" },
  { kicker: "Makeup", to: "/shop/makeup", headline: "Color that feels like you", image: "/media/hero/swatches.webp", mImage: "/media/hero/swatches-m.webp", alt: "Crushed makeup swatches in brown, pink and gold", bg: "#0B0A0A", w: 1628, h: 966, mw: 2200, mh: 1306, layout: "left", textW: "lg:w-[44%]", light: true },
];

// Phones use a version of each photo whose backdrop is extended upward/sideways so it fills the whole card seamlessly
const PHONE_MQ = "(max-width: 639px)";

// Photo stays whole: phones/tablets = bottom-centred under the text; desktop = full-height on the right, or framed on the left
const PHOTO_CLS = {
  right: "lg:left-auto lg:right-0 lg:top-0 lg:bottom-auto lg:translate-x-0 lg:h-full lg:w-auto lg:max-h-none lg:max-w-none hero-photo--right",
  left: "lg:left-0 lg:top-1/2 lg:bottom-auto lg:translate-x-0 lg:-translate-y-1/2 lg:max-w-[60%] lg:max-h-[90%] hero-photo--frame",
};
const TEXT_CLS = { right: "lg:right-auto", left: "lg:left-auto" };

const N = SLIDES.length;
const ITEMS = N * 2; // two copies so the wrap-around jump always happens off-screen

const offsetOf = (k, pos) => {
  const o = (((k - pos) % ITEMS) + ITEMS) % ITEMS;
  return o > ITEMS / 2 ? o - ITEMS : o;
};


function SlideCard({ slide, index, active, clone }) {
  const tone = slide.light
    ? { kicker: "text-white/80", head: "text-white", btn: "bg-white text-[#111111] hover:bg-white/85" }
    : { kicker: "text-[#111111]/80", head: "text-[#111111]", btn: "bg-[#111111] text-white hover:bg-[#333333]" };
  return (
    <div className="relative h-full rounded-[22px] md:rounded-[28px] overflow-hidden" style={{ backgroundColor: slide.bg }}>
      <picture>
        <source media={PHONE_MQ} srcSet={slide.mImage} width={slide.mw} height={slide.mh} />
        <img
          src={slide.image}
          alt={slide.alt}
          width={slide.w}
          height={slide.h}
          draggable={false}
          loading={index === 0 && !clone ? "eager" : "lazy"}
          className={`hero-photo absolute bottom-0 left-1/2 -translate-x-1/2 w-auto h-[112%] max-w-none sm:h-auto sm:max-w-full sm:max-h-[56%] select-none ${PHOTO_CLS[slide.layout]}`}
          data-testid={clone ? undefined : `hero-slide-image-${index}`}
        />
      </picture>
      <div className={`absolute top-0 inset-x-0 z-10 flex flex-col items-center text-center px-6 pt-8 sm:pt-12 lg:inset-y-0 lg:justify-center lg:pt-0 lg:px-8 xl:px-10 ${slide.textW} ${TEXT_CLS[slide.layout]}`}>
        <p className={`font-sans text-[15px] sm:text-base lg:text-lg font-medium ${tone.kicker}`}>{slide.kicker}</p>
        <h2
          className={`font-sans font-bold tracking-[-0.035em] leading-[1.04] text-[30px] sm:text-[40px] lg:text-[clamp(36px,3.3vw,56px)] mt-2 lg:mt-4 ${tone.head}`}
          data-testid={clone ? undefined : `hero-slide-headline-${index}`}
        >
          {slide.headline}
        </h2>
        <Link
          to={slide.to}
          onClick={(e) => e.stopPropagation()}
          tabIndex={active ? 0 : -1}
          data-testid={clone ? undefined : `hero-shop-now-${index}`}
          className={`mt-5 lg:mt-9 inline-flex items-center justify-center h-12 lg:h-14 px-9 lg:px-11 rounded-full font-sans font-semibold text-[15px] lg:text-base transition-colors duration-300 ${tone.btn}`}
        >
          Shop Now
        </Link>
      </div>
    </div>
  );
}

function Dots({ active, onDot }) {
  return (
    <div className="flex items-center justify-center gap-1 mt-2 md:mt-4" data-testid="hero-dots">
      {SLIDES.map((s, i) => (
        <button key={s.headline} onClick={() => onDot(i)} aria-label={`Go to slide ${i + 1}`} aria-current={active === i} data-testid={`hero-dot-${i}`} className="min-w-[44px] h-11 px-1 flex items-center justify-center">
          <span className={`block h-2.5 rounded-full transition-all duration-500 ${active === i ? "w-12 bg-[#111111]" : "w-2.5 bg-[#333333]/20 hover:bg-[#333333]/40"}`} />
        </button>
      ))}
    </div>
  );
}

export default function HomeHeroCarousel() {
  const [{ pos, dir }, setNav] = useState({ pos: 0, dir: 0 });
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const active = ((pos % N) + N) % N;
  const step = (d) => setNav((s) => ({ pos: s.pos + d, dir: d }));

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => step(1), 6500);
    return () => clearTimeout(t);
  }, [pos, paused]);

  const onDot = (i) => {
    const d = (i - active + N) % N;
    if (d) step(d === N - 1 ? -1 : 1);
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const diff = touchX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) step(diff > 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section
      className="bg-[#FBF7F3] pt-[112px] sm:pt-[120px] pb-4 md:pb-8"
      data-testid="hero-section"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <h1 className="sr-only">DORIA — Beauty, Reimagined.</h1>
      <div
        className="relative overflow-hidden h-[460px] sm:h-[600px] lg:h-[clamp(420px,42vw,620px)]"
        data-testid="hero-track"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={onTouchEnd}
      >
        {Array.from({ length: ITEMS }).map((_, k) => {
          const offset = offsetOf(k, pos);
          const jump = (dir > 0 && offset === ITEMS / 2) || (dir < 0 && offset === -(ITEMS / 2 - 1));
          const isPeek = Math.abs(offset) === 1;
          return (
            <div
              key={k}
              className={`absolute top-0 left-1/2 h-full w-[calc(100%-32px)] sm:w-[84%] lg:w-[80%] max-w-[1440px] [--hero-gap:24px] sm:[--hero-gap:16px] ${isPeek ? "cursor-pointer" : ""}`}
              style={{
                transform: `translateX(calc(-50% + ${offset} * (100% + var(--hero-gap))))`,
                transition: jump ? "none" : "transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
              }}
              aria-hidden={offset !== 0}
              data-testid={k < N ? `hero-slide-${k}` : undefined}
              data-active={offset === 0}
              data-slide-index={k % N}
              onClick={isPeek ? () => step(offset) : undefined}
            >
              <SlideCard slide={SLIDES[k % N]} index={k % N} active={offset === 0} clone={k >= N} />
            </div>
          );
        })}
      </div>
      <Dots active={active} onDot={onDot} />
    </section>
  );
}
